import { LitElement, html, internalProperty, property } from 'lit-element'
import { register } from '@polleverywhere/hmr-custom-element'
import { defineElementSlot } from '@polleverywhere/element-helpers'
import { ifDefined } from 'lit-html/directives/if-defined'
import { connect } from 'pwa-helpers/connect-mixin'
import store from '@polleverywhere/redux'
import '@polleverywhere/pe-navigation'
import { slice as appSlice } from '../../slices/app'
import { slice as profileSlice } from '@polleverywhere/redux/slices/profile'
import './style.scss'

const L1Map = {
  home: {
    name: 'home',
    icon: 'home',
    label: 'Home'
  },
  activities: {
    name: 'activities',
    icon: 'activities',
    label: 'Activities',
    defaultL2: 'activities/activities'
  },
  support: {
    name: 'support',
    icon: 'support',
    label: 'Support'
  },
  settings: {
    name: 'settings',
    icon: 'gear',
    label: 'Settings'
  },
  login: {
    name: 'login',
    icon: 'profile',
    label: 'Login'
  },
  logout: {
    name: 'logout',
    icon: 'exit',
    label: 'Logout'
  },
  history: {
    name: 'response_history',
    icon: 'response-history',
    label: 'History'
  },
  registration: {
    name: 'registrations',
    icon: 'registration',
    label: 'Registration'
  },
  present: {
    name: 'present',
    icon: 'activity',
    label: 'Present'
  }
}

// TODO: do we need to make a L2 for settings? there's only one page right now
// also: how are we loading the separate L2 navs? will this only work with folders?
function getL2For (route) {
  if (route === 'activities') {
    const items = []
    items.push({
      name: 'activities/activities',
      label: 'My activities',
      description: 'Come here for all your activities'
    })

    if (profileSlice.selectUserFeatures(store.getState()).shared_activities) {
      items.push({
        name: 'activities/shared',
        label: 'Shared activities',
        description: 'Here are all the activities shared with you'
      })
    }

    if (this.role !== 'agave') {
      items.push({
        name: 'activities/trash',
        label: 'Trash',
        description: 'Restore activities here or delete them forever'
      })
    }

    return { items, showToggle: true }
  }
}

/**
 * Renders a wrapper for `pe-navigation` element.
 *
 * @prop {string} active - Current active page.
 *
 * @fires {Object} [placeholder] - placeholder
 */
export default class Navigation extends connect(store)(LitElement) {
  static get tagName () { return 'nav-wrapper' }

  @property({ type: String })
  active

  @internalProperty({ type: Boolean })
  allowRedirects

  @internalProperty({ type: String })
  mode

  @internalProperty({ type: String })
  role

  @internalProperty({ type: Boolean })
  isLoggedIn

  @internalProperty({ type: Boolean })
  showL1 = true

  @internalProperty({ type: Boolean })
  showL2 = true

  @internalProperty({ type: Boolean })
  openL2 = true

  constructor () {
    super()
    this.role = store.getState()[appSlice.name].role
  }

  connectedCallback () {
    super.connectedCallback()
    defineElementSlot(this)
  }

  createRenderRoot () { return this }

  stateChanged (state) {
    this.allowRedirects = state[appSlice.name].options.allowRedirects
    this.mode = state[appSlice.name].mode
    this.role = state[appSlice.name].role
    this.isLoggedIn = appSlice.selectIsFullyLoggedIn(state)
    this.showL1 = state[appSlice.name].showL1
    this.showL2 = state[appSlice.name].showL2
    this.openL2 = state[appSlice.name].openL2
  }

  render () {
    if (!this.showL1) {
      return ''
    }

    // active can be a path
    const l1Path = this.active.split('/')[0]
    const isTouchDevice = window?.navigator?.maxTouchPoints > 0
    const showLabels = this.role === 'participant'

    return html`
      <pe-navigation
        active=${ifDefined(l1Path)}
        ?hide-tooltips=${isTouchDevice}
        ?show-labels=${showLabels}>
          ${generateL1.call(this)}
        <div slot='l2'>
          ${this.showL2 ? generateL2Panel.call(this, l1Path) : ''}
        </div>
      </pe-navigation>
    `
  }
}

function generateL1 () {
  if (this.role === 'agave') {
    return html`
      ${generateL1Item('activities')}
      ${generateL1Item('logout')}
    `
  }

  if (this.role === 'participant') {
    const inApp = true || !!window.Capacitor?.isNative
    return html`
      ${generateL1Item('home')}
      ${generateL1Item('history')}
      ${generateL1Item('registration')}
      ${inApp
        ? ''
        : generateL1Item('present')
      }
      ${this.isLoggedIn
        ? generateL1Item('settings')
        : generateL1Item('login')
      }
    `
  }

  return html`
    ${generateL1Item('home')}
    ${generateL1Item('activities')}
    ${this.allowRedirects && this.mode !== 'integration'
      ? generateL1Item('support')
      : ''}
    ${generateL1Item('settings')}
  `
}

function generateL1Item (name) {
  return html`
    <pe-navigation-item
      description=${ifDefined(L1Map[name].description)}
      label=${ifDefined(L1Map[name].label)}
      icon=${ifDefined(L1Map[name].icon)}
      name=${ifDefined(L1Map[name].name)}
      viewports='sm md'
    ></pe-navigation-item>
  `
}

function generateL2Panel (route) {
  const l2 = getL2For.call(this, route)
  if (!l2) return

  // default active item
  let active = L1Map[route].defaultL2

  // if a deep active path was specified, use that
  const paths = this.active.split('/')
  if (paths[0] === route) {
    active = paths.slice(1).join('/')
  }

  return html`
    <pe-navigation-panel
      ?show-toggle=${l2.showToggle}
      ?opened=${this.openL2}
      active=${active}
      heading=${ifDefined(L1Map[route].label)}
      icon=${ifDefined(l2.icon)}>
      ${l2.items.map((item) => {
        return html`
          <pe-navigation-item
            description=${ifDefined(item.description)}
            label=${ifDefined(item.label)}
            name=${ifDefined(item.name)}
            viewports='sm md'
          >
          </pe-navigation-item>
        `
      })}
    </pe-navigation-panel>
  `
}

register(module, Navigation.tagName, Navigation)
